/* eslint-disable max-lines */
import { getShipmentEventHistoryApi, listOrdersApi } from 'api/order';
import { getRequestByReferenceIdApi, listPomRequestsStatusApi } from 'api/orderStatus';
import { getPatientApi, listLineItemNotesApi, listPatientOrdersApi } from 'api/patient';
import orderStatus from 'common/constants/orderStatus';
import { find, first, isEmpty, keys } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { patientActions } from 'store/slices/patientSlice';
import { handleError } from 'store/thunks/errorHandlerThunks';

/**
 * Fetches and manages all data needed by OrderViewDialog.
 * Returns the order object, supplemental data, and helpers.
 */
const useOrderData = ({ orderId, historyEnabled = false }) => {
  const dispatch = useDispatch();

  const [order, setOrder] = useState(null);
  const [patient, setPatient] = useState(null);
  const [events, setEvents] = useState([]);
  const [requestEvents, setRequestEvents] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [practiceName, setPracticeName] = useState(null);
  const [partnerId, setPartnerId] = useState(null);
  const [practiceId, setPracticeId] = useState(null);
  const [orderAddress, setOrderAddress] = useState(null);
  const [deliveryInstructions, setDeliveryInstructions] = useState(null);
  const [initialFulfillmentAddress, setInitialFulfillmentAddress] = useState(null);
  const [patientExternalId, setPatientExternalId] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [reloading, setReloading] = useState(false);
  const [enriching, setEnriching] = useState(false);

  // Tracks which orderId supplemental data (patient, history, POM) was last fetched for.
  // Prevents re-fetching when reloadOrder only updates line item data.
  const supplementalFetchedForRef = useRef(null);
  // Tracks which orderId history (events + POM) was last fetched for (lazy).
  const historyFetchedForRef = useRef(null);

  // Holds the resolve function for the promise returned by reloadOrder().
  // Callers await this to keep their loading state until enriched data lands.
  const reloadResolveRef = useRef(null);

  const reloadOrder = useCallback(
    () =>
      new Promise((resolve) => {
        reloadResolveRef.current = resolve;
        setReloadToken((t) => t + 1);
      }),
    []
  );

  const timelineBottomRef = useRef(null);

  // Fetch the base order, show it immediately, then enrich line items in background.
  // reloadToken is incremented by reloadOrder() to force a re-fetch after mutations.
  useEffect(() => {
    if (!orderId) {
      setOrder(null);
      setNotFound(false);
      supplementalFetchedForRef.current = null;
      historyFetchedForRef.current = null;
      return;
    }
    const isReload = reloadToken > 0;
    if (!isReload) {
      setOrder(null);
      supplementalFetchedForRef.current = null;
      historyFetchedForRef.current = null;
    }
    setLoading(!isReload);
    setReloading(isReload);
    setNotFound(false);
    listOrdersApi({
      page: 1,
      filter: {
        orderId,
        orderStatus: keys(orderStatus),
      },
      sortBy: { sortKey: 'orderId', sortOrder: 'DESC' },
    })
      .then(({ orders }) => {
        if (isEmpty(orders)) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        const found = first(orders);
        // On first load: show the base order immediately, then enrich in the background.
        // On reload: skip the intermediate base-order update to avoid column count changes
        // (e.g. showLabelColumn flickering) that cause layout shifts. Wait for enriched data.
        if (!isReload) {
          setOrder(found);
          setLoading(false);
          setReloading(false);
          setEnriching(true);
        }

        // Enrich line items in the background and patch when ready.
        // No status filter — we need to find this specific order regardless of its status
        // (e.g. CANCELED orders would be excluded if we filter by orderStatus).
        listPatientOrdersApi({
          mpi: found.mpi,
          filter: { orderId: found.orderId, orderStatus: keys(orderStatus) },
        })
          .then((patientOrders) => {
            // Populate Redux so child components that read patient[mpi]?.orders (e.g.
            // SendToShippingButton, ReshipOrderButton) can find the order.
            dispatch(patientActions.loadOrders({ mpi: found.mpi, orders: patientOrders }));
            // Also populate orderHistory so ReshipOrderButton can read the shipping address.
            dispatch(patientActions.loadOrderHistory({ mpi: found.mpi, orders: patientOrders }));

            const patientOrder = find(patientOrders, { orderId: found.orderId });
            if (patientOrder?.shippingAddress) {
              setOrderAddress(patientOrder.shippingAddress);
              setDeliveryInstructions(patientOrder.deliveryInstructions || null);
            }
            setOrder({
              ...found,
              lineItems: patientOrder?.lineItems ?? found.lineItems,
              orderType: patientOrder?.orderType,
              isProcessedExternal: patientOrder?.isProcessedExternal ?? found.isProcessedExternal,
              fillType: patientOrder?.fillType ?? found.fillType,
              partner: patientOrder?.partner ?? found.partner,
              practice: patientOrder?.practice ?? found.practice,
              partnerAccountId: patientOrder?.partnerAccountId ?? found.partnerAccountId,
              reshipmentOf: patientOrder?.reshipmentOf ?? null,
              fillLaterOf: patientOrder?.fillLaterOf ?? null,
              isFillLater: patientOrder?.isFillLater ?? false,
            });
            setEnriching(false);
            if (isReload) {
              setLoading(false);
              setReloading(false);
              reloadResolveRef.current?.();
              reloadResolveRef.current = null;
            }
          })
          .catch(() => {
            // Enrichment failed — keep the existing order as-is on reload, base order on first load.
            setEnriching(false);
            if (isReload) {
              setLoading(false);
              setReloading(false);
              reloadResolveRef.current?.();
              reloadResolveRef.current = null;
            }
          });
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        setLoading(false);
        setReloading(false);
        reloadResolveRef.current?.();
        reloadResolveRef.current = null;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, reloadToken, dispatch]);

  // Once the order is loaded, fetch patient and notes immediately (always eager).
  // History (events + POM) is deferred — see the effect below.
  useEffect(() => {
    if (!order) return;
    // Skip if supplemental data was already loaded for this orderId (e.g. after a line item reload).
    if (supplementalFetchedForRef.current === order.orderId) return;
    supplementalFetchedForRef.current = order.orderId;

    setPatient(null);
    setEvents([]);
    setRequestEvents([]);
    setComments([]);
    setPracticeName(null);
    setPartnerId(null);
    setPracticeId(null);
    setOrderAddress(null);
    setDeliveryInstructions(null);
    setInitialFulfillmentAddress(null);
    setPatientExternalId(null);

    // Patient: independent, renders as soon as it arrives.
    getPatientApi({ mpi: order.mpi })
      .then((patientData) => {
        setPatient(patientData);
        if (patientData) {
          const { addresses, ...rest } = patientData;
          // Populate Redux patient record so components reading from the store
          // (e.g. PatientBanner in the fill dialog) can display patient details.
          dispatch(patientActions.loadPatientData({ patient: { mpi: order.mpi, ...rest } }));
          // Populate Redux addresses so ReshipOrderButton can display the address list.
          if (addresses) {
            dispatch(patientActions.loadAddresses({ mpi: order.mpi, addresses }));
          }
        }
      })
      .catch((error) => dispatch(handleError({ error })));

    // Notes tab: always load eagerly.
    setNotesLoading(true);
    listLineItemNotesApi({
      mpi: order.mpi,
      contextType: 'ORDER',
      filter: { noteContext: { noteContextIds: [order.orderId], type: 'ORDER' } },
    })
      .then((commentData) => {
        setComments(commentData || []);
        setTimeout(() => timelineBottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      })
      .catch((error) => dispatch(handleError({ error })))
      .finally(() => setNotesLoading(false));
  }, [order, dispatch]);

  // History tab: fetch events and POM only when the tab is first activated (lazy).
  // The ref guard ensures we only fetch once per orderId even if historyEnabled flips.
  useEffect(() => {
    if (!order || !historyEnabled) return;
    if (historyFetchedForRef.current === order.orderId) return;
    historyFetchedForRef.current = order.orderId;

    setHistoryLoading(true);
    const pomRequestPromise = listPomRequestsStatusApi({
      page: 1,
      filter: { pomOrderId: order.orderId },
    })
      .then(({ result }) => {
        const pomRequest = first(result);
        if (!pomRequest) return null;
        return getRequestByReferenceIdApi({
          referenceId: pomRequest.referenceId,
          requestId: pomRequest.requestId,
          orderId: order.orderId,
          partnerId: pomRequest.partnerId,
        }).then((requestDetails) => ({ requestDetails, partnerId: pomRequest.partnerId }));
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        return null;
      });

    Promise.all([
      getShipmentEventHistoryApi({ orderId: order.orderId }).catch((error) => {
        dispatch(handleError({ error }));
        return null;
      }),
      pomRequestPromise,
    ])
      .then(([eventData, pomData]) => {
        setEvents([
          ...(eventData?.events || []).map((e) => ({ ...e, isShipmentError: false })),
          ...(eventData?.errors || []).map((e) => ({ ...e, isShipmentError: true })),
        ]);
        if (pomData) {
          setRequestEvents(pomData.requestDetails?.events || []);
          setPracticeName(pomData.requestDetails?.practice?.practiceName || null);
          setPracticeId(pomData.requestDetails?.practice?.practiceId || null);
          setPartnerId(pomData.partnerId || null);
          setOrderAddress((prev) => ({
            ...(pomData.requestDetails?.shippingAddress || null),
            nameOnAddress: prev?.nameOnAddress ?? null,
          }));
          setDeliveryInstructions(pomData.requestDetails?.deliveryInstructions || null);
          setPatientExternalId(pomData.requestDetails?.patient?.externalPatientId || null);
          const requestEvtsData = pomData.requestDetails?.events || [];
          const initEventData = find(requestEvtsData, ({ actionItemType }) =>
            ['ORDER_SUBMITTED_FROM', 'REFILL_ORDER_CREATED_FROM'].some((type) =>
              actionItemType.includes(type)
            )
          )?.interactionData;
          setInitialFulfillmentAddress(
            initEventData?.originalEventPayload?.fulfillmentDetails?.shippingAddress ||
              initEventData?.constructedPayload?.fulfillmentDetails?.shippingAddress ||
              initEventData?.originalEventPayload?.shippingInput ||
              null
          );
        }
      })
      .catch((error) => dispatch(handleError({ error })))
      .finally(() => setHistoryLoading(false));
  }, [order, historyEnabled, dispatch]);

  const reloadOrderAddress = useCallback(() => {
    if (!order) return Promise.resolve();
    return listPatientOrdersApi({
      mpi: order.mpi,
      filter: { orderId: order.orderId, orderStatus: keys(orderStatus) },
    })
      .then((patientOrders) => {
        const patientOrder = find(patientOrders, { orderId: order.orderId });
        if (patientOrder?.shippingAddress) {
          setOrderAddress(patientOrder.shippingAddress);
          setDeliveryInstructions(patientOrder.deliveryInstructions || null);
        }
      })
      .catch((error) => dispatch(handleError({ error })));
  }, [order, dispatch]);

  const reloadComments = useCallback(() => {
    if (!order) return Promise.resolve();
    return listLineItemNotesApi({
      mpi: order.mpi,
      contextType: 'ORDER',
      filter: { noteContext: { noteContextIds: [order.orderId], type: 'ORDER' } },
    })
      .then((data) => setComments(data || []))
      .catch((error) => dispatch(handleError({ error })));
  }, [order, dispatch]);

  const reloadHistory = useCallback(() => {
    if (!order || !historyEnabled) return Promise.resolve();

    const pomRequestPromise = listPomRequestsStatusApi({
      page: 1,
      filter: { pomOrderId: order.orderId },
    })
      .then(({ result }) => {
        const pomRequest = first(result);
        if (!pomRequest) return null;
        return getRequestByReferenceIdApi({
          referenceId: pomRequest.referenceId,
          requestId: pomRequest.requestId,
          orderId: order.orderId,
          partnerId: pomRequest.partnerId,
        }).then((requestDetails) => ({ requestDetails, partnerId: pomRequest.partnerId }));
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        return null;
      });

    return Promise.all([
      getShipmentEventHistoryApi({ orderId: order.orderId }).catch((error) => {
        dispatch(handleError({ error }));
        return null;
      }),
      pomRequestPromise,
    ]).then(([eventData, pomData]) => {
      setEvents([
        ...(eventData?.events || []).map((e) => ({ ...e, isShipmentError: false })),
        ...(eventData?.errors || []).map((e) => ({ ...e, isShipmentError: true })),
      ]);
      if (pomData) {
        setRequestEvents(pomData.requestDetails?.events || []);
      }
    });
  }, [order, historyEnabled, dispatch]);

  return {
    order,
    patient,
    events,
    requestEvents,
    comments,
    loading,
    reloading,
    enriching,
    historyLoading,
    notesLoading,
    practiceName,
    partnerId,
    practiceId,
    orderAddress,
    deliveryInstructions,
    initialFulfillmentAddress,
    patientExternalId,
    timelineBottomRef,
    reloadComments,
    reloadHistory,
    reloadOrderAddress,
    reloadOrder,
    notFound,
  };
};

export default useOrderData;
